<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="50%"
    :close-on-click-modal="false"
    :before-close="handleClose"
    class="protocol-edit-dialog"
  >
    <div class="protocol-form">
      <el-form :model="form" ref="protocolForm" label-width="140px" :rules="rules">
        <!-- 协议类型 -->
        <el-form-item label="协议类型：" required>
          <el-radio-group v-model="form.target_type" @change="handleTypeChange" :disabled="isView">
            <el-radio :label="1">产品协议</el-radio>
            <el-radio :label="2">平台服务协议</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 产品协议相关字段 -->
        <template v-if="form.target_type === 1">
          <el-form-item label="产品名称：" prop="product_id" required>
            <el-select 
              v-model="form.product_id" 
              placeholder="请选择产品名称"
              :disabled="isView"
              @change="handleProductChange"
              style="width: 200px;"
            >
              <el-option
                v-for="item in productList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="价格方案：" v-if="!isEdit">
            <el-select 
              v-model="form.price_id" 
              placeholder="请选择价格方案"
              :disabled="isView"
              multiple
              style="width: 200px;"
            >
              <el-option
                v-for="item in priceList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="价格方案：" v-else>
            <el-select 
              v-model="form.price_id_single" 
              placeholder="请选择价格方案"
              :disabled="isView"
              style="width: 200px;"
            >
              <el-option
                v-for="item in priceList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </template>

        <!-- 平台服务协议相关字段 -->
        <template v-if="form.target_type === 2">
          <el-form-item label="平台名称：" prop="app_id" required>
            <el-select 
              v-model="form.app_id" 
              placeholder="请选择平台名称"
              :disabled="isView"
              style="width: 200px;"
            >
              <el-option
                v-for="item in appList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </template>

        <!-- 基本信息 -->
        <el-form-item label="条约名称：" prop="title" required>
          <el-input 
            v-model="form.title" 
            placeholder="请输入条约名称"
            :disabled="isView"
            style="width: 300px;"
          />
        </el-form-item>

        <el-form-item label="条约版本号：" prop="version" required>
          <el-input 
            v-model="form.version" 
            placeholder="请输入条约版本号"
            :disabled="isView"
            style="width: 200px;"
          />
        </el-form-item>

        <el-form-item label="状态：" required>
          <el-radio-group v-model="form.disabled" :disabled="isView">
            <el-radio :label="false">发布</el-radio>
            <el-radio :label="true">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="是否必须签署：" required>
          <el-radio-group v-model="form.required" :disabled="isView">
            <el-radio :label="true">是</el-radio>
            <el-radio :label="false">否</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 富文本编辑器 -->
        <el-form-item label="条约内容：" prop="content" required>
          <quill-editor 
            ref="myQuillEditor" 
            v-model="form.content"
            :options="editorOption"
            :disabled="isView"
            style="height: 300px;"
          />
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button v-if="!isView" type="primary" @click="handleSave" :loading="saving">保 存</el-button>
      <el-button @click="handleCancel">{{ isView ? '关 闭' : '取 消' }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

import { 
  saveProtocol, 
  getProtocolInfo,
  getProductList, 
  getAppList,
  getPriceList 
} from '@/api/product'

export default {
  name: 'ProtocolEditDialog',
  components: {
    quillEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    protocolData: {
      type: Object,
      default: null
    },
    isView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      saving: false,
      form: {
        id: null,
        target_type: 1,
        product_id: null,
        price_id: [],
        price_id_single: null,
        app_id: null,
        title: '',
        version: '',
        disabled: false,
        required: false,
        content: ''
      },
      productList: [],
      appList: [],
      priceList: [],
      editorOption: {
        // quill 编辑器配置
        placeholder: '请输入条约内容...',
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],               // custom button values
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
            [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
            [{ 'direction': 'rtl' }],                         // text direction
            [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],                                         // remove formatting button
            ['link', 'image']
          ]
        }
      },
      rules: {
        product_id: [
          { required: true, message: '请选择产品名称', trigger: 'change' }
        ],
        app_id: [
          { required: true, message: '请选择平台名称', trigger: 'change' }
        ],
        title: [
          { required: true, message: '请输入条约名称', trigger: 'blur' }
        ],
        version: [
          { required: true, message: '请输入条约版本号', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入条约内容', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      if (this.isView) return '查看协议'
      return this.isEdit ? '编辑条约' : '添加条约'
    },
    isEdit() {
      return !!(this.protocolData && this.protocolData.id)
    }
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val
        if (val) {
          this.initDialog()
        }
      },
      immediate: true
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    // 初始化对话框
    async initDialog() {
      await this.loadInitialData()
      if (this.protocolData && this.protocolData.id) {
        await this.loadProtocolData()
      } else {
        this.resetForm()
      }
    },

    // 加载初始数据
    async loadInitialData() {
      try {
        const [productResponse, appResponse] = await Promise.all([
          getProductList(),
          getAppList()
        ])

        if (productResponse && productResponse.err_code === 0) {
          this.productList = productResponse.rows || []
        }

        if (appResponse && appResponse.err_code === 0) {
          this.appList = appResponse.rows || []
        }
      } catch (error) {
        console.error('加载初始数据失败:', error)
      }
    },

    // 加载协议数据
    async loadProtocolData() {
      try {
        const response = await getProtocolInfo({ id: this.protocolData.id })
        if (response && response.err_code === 0) {
          const data = response.data
          
          this.form.id = data.id
          this.form.title = data.title || ''
          this.form.version = data.version || ''
          this.form.disabled = !!data.disabled
          this.form.required = !!data.required
          this.form.content = data.content || ''

          // 根据数据判断协议类型
          if (data.product_id && data.product_id > 0) {
            this.form.target_type = 1
            this.form.product_id = data.product_id
            this.form.price_id_single = data.price_id
            
            // 加载价格方案列表
            await this.loadPriceList(data.product_id)
          } else if (data.app_id) {
            this.form.target_type = 2
            this.form.app_id = data.app_id
          }
        }
      } catch (error) {
        console.error('加载协议数据失败:', error)
        this.$message.error('加载数据失败')
      }
    },

    // 加载价格方案列表
    async loadPriceList(productId) {
      if (!productId) return
      
      try {
        const response = await getPriceList({ product_id: productId })
        if (response && response.err_code === 0) {
          this.priceList = response.rows || []
        }
      } catch (error) {
        console.error('加载价格方案列表失败:', error)
      }
    },

    // 协议类型改变
    handleTypeChange(value) {
      // 清空相关字段
      if (value === 1) {
        this.form.app_id = null
      } else {
        this.form.product_id = null
        this.form.price_id = []
        this.form.price_id_single = null
        this.priceList = []
      }
    },

    // 产品改变
    async handleProductChange(productId) {
      this.form.price_id = []
      this.form.price_id_single = null
      this.priceList = []
      
      if (productId) {
        await this.loadPriceList(productId)
      }
    },

    // 保存
    async handleSave() {
      try {
        await this.$refs.protocolForm.validate()
      } catch (error) {
        return
      }

      // 验证协议类型相关字段
      if (this.form.target_type === 1) {
        if (!this.form.product_id) {
          this.$message.warning('请选择产品名称')
          return
        }
      } else if (this.form.target_type === 2) {
        if (!this.form.app_id) {
          this.$message.warning('请选择平台名称')
          return
        }
      }

      if (!this.form.content.trim()) {
        this.$message.warning('请输入条约内容')
        return
      }

      this.saving = true
      try {
        const data = {
          title: this.form.title,
          version: this.form.version,
          disabled: this.form.disabled,
          required: this.form.required,
          content: this.form.content
        }

        if (this.form.id) {
          data.id = this.form.id
        }

        if (this.form.target_type === 1) {
          data.product_id = this.form.product_id
          
          // 根据是否编辑模式处理价格方案
          if (this.isEdit) {
            data.price_id = this.form.price_id_single
          } else {
            data.price_id = this.form.price_id
          }
        } else if (this.form.target_type === 2) {
          data.type = 1
          data.app_id = this.form.app_id
        }

        const response = await saveProtocol(data)
        if (response && response.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('save', response.data)
        } else {
          this.$message.error(response?.err_msg || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.saving = false
      }
    },

    // 取消
    handleCancel() {
      this.handleClose()
    },

    // 关闭对话框
    handleClose() {
      this.$emit('cancel')
      this.resetForm()
    },

    // 重置表单
    resetForm() {
      this.form = {
        id: null,
        target_type: 1,
        product_id: null,
        price_id: [],
        price_id_single: null,
        app_id: null,
        title: '',
        version: '',
        disabled: false,
        required: false,
        content: ''
      }
      
      if (this.$refs.protocolForm) {
        this.$refs.protocolForm.clearValidate()
      }
    }
  }
}
</script>

<style scoped>
.protocol-edit-dialog ::v-deep .el-dialog__body {
  padding: 20px 20px 0 20px;
}

.protocol-form {
  max-height: 70vh;
  overflow-y: auto;
}

.dialog-footer {
  text-align: center;
  padding: 10px 0;
}

.el-form-item {
  margin-bottom: 20px;
}

.el-radio-group {
  line-height: 32px;
}

/* 表单样式优化 */
.protocol-edit-dialog ::v-deep .el-form-item__label {
  font-weight: normal;
}

.protocol-edit-dialog ::v-deep .el-textarea__inner {
  font-family: inherit;
}

/* quill 编辑器样式 */
.protocol-edit-dialog ::v-deep .quill-editor {
  margin-bottom: 20px;
}

.protocol-edit-dialog ::v-deep .ql-editor {
  min-height: 200px;
  max-height: 300px;
  overflow-y: auto;
}

.protocol-edit-dialog ::v-deep .ql-toolbar {
  border-top: 1px solid #ccc;
}

.protocol-edit-dialog ::v-deep .ql-container {
  border-bottom: 1px solid #ccc;
}
</style>